import { Component, TemplateFunction } from '@ribajs/core'

/**
 * The types for the scope
 */
interface Scope {
  // OPTIONS
  confirmMessage: string
  showNewAddressForm: boolean

  // METHODS
  add: SbCustomerAddressesComponent['add']
  cancel: SbCustomerAddressesComponent['cancel']
  edit: SbCustomerAddressesComponent['edit']
  delete: SbCustomerAddressesComponent['delete']

  // TEMPLATES

  // INPUTS
  form: {
    customer: {
      email: string
      password: string
    }
  }
}

/**
 * A simple template for new component, just make a copy of this template, adjust the class / tag name and add a new export to `src/riba/components/index.ts`.
 */
export class SbCustomerAddressesComponent extends Component {
  /**
   * The tag name of the element, in this case `<sb-customer-addresses foobar="hello">...</sb-customer-addresses>`
   */
  public static tagName = 'sb-customer-addresses'

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   * Examples:
   * - Methods can be called with `<div rv-on-click="show"></div>`, this will call the show method defined in the scope
   * - Properties can be used with `<div rv-click-active="visible"></div>`, this will add the class `active` if visible is `true` and remove the class `active` if visible is `false`,
   * - Templates can be used with `<div rv-template="body"></div>`, `<div rv-html="body"></div>` or `<div rv-text="body"></div>`, this will be add the template, html or string to the innerHTML of the element. But you can only use the Riba Template Engine with `rv-template`
   */
  public scope: Scope = {
    // OPTIONS
    confirmMessage: '',
    showNewAddressForm: false,

    // METHODS
    add: this.add,
    cancel: this.cancel,
    edit: this.edit,
    delete: this.delete,

    // TEMPLATES

    // INPUTS
    form: {
      customer: {
        email: '',
        password: '',
      },
    },
  }

  /**
   * Attributes that can be passed to the custom element.
   * @note These are automatically added to the scope and parsed to string, number, boolean, object, etc
   */
  static get observedAttributes(): string[] {
    return ['confirm-message']
  }

  /**
   * Attributes that must be passed to the custom element, if not all attributes are truthy,
   * the component will not be initialized and an error message will be issued.
   * @returns
   */
  protected requiredAttributes(): string[] {
    return ['confirm-message']
  }

  /**
   * Called as soon as this custom element es registered as a available custom element
   */
  constructor() {
    super()
  }

  /** Add new address */
  public add() {
    this.scope.showNewAddressForm = true
  }

  /** Edit existing address by showing add new address form */
  public edit(
    addressId: number,
    event: Event,
    scope: Scope,
    el: HTMLButtonElement,
  ) {
    // Toggle aria-expanded attribute
    const ariaExpanded = el.getAttribute('aria-expanded') === 'true'
    el.setAttribute('aria-expanded', (!ariaExpanded).toString())
  }

  /** Cancel add new address */
  public cancel() {
    this.scope.showNewAddressForm = false

    // Also cancel all other
    const expandedEls = Array.from(
      this.querySelectorAll('[aria-expanded=true]'),
    )
    for (const expandedEl of expandedEls) {
      expandedEl.setAttribute('aria-expanded', 'false')
    }
  }

  /** Delete existing address */
  public delete(path: string) {
    if (confirm(this.scope.confirmMessage)) {
      this.postLink(path, {
        parameters: { _method: 'delete' },
      })
    }
  }

  protected postLink(path: string, options: any) {
    options = options || {}
    const method = options['method'] || 'post'
    const params = options['parameters'] || {}

    const form = document.createElement('form')
    form.setAttribute('method', method)
    form.setAttribute('action', path)

    for (const key in params) {
      const hiddenField = document.createElement('input')
      hiddenField.setAttribute('type', 'hidden')
      hiddenField.setAttribute('name', key)
      hiddenField.setAttribute('value', params[key])
      form.appendChild(hiddenField)
    }
    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)
  }

  /**
   * Called as soon as this custom element es detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCustomerAddressesComponent.observedAttributes)
    this.addEventListeners()
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  async beforeBind() {
    await super.beforeBind()
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbCustomerAddressesComponent.tagName}-ready`)
  }

  protected addEventListeners() {
    // Nothing yet
  }

  protected removeEventListeners() {
    // Nothing yet
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced, otherwise the innerHTML is replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
